import { cn } from "@-/com.npmjs.cn";
import {
  WebsiteCard,
  WebsiteContainer,
  WebsiteEyebrow,
  WebsiteHeading,
  WebsiteSection,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";

type LandingTone = "high" | "falling";

// The app's default tone colors (tone 1 red, tone 4 purple) as fixed `landing-tone-*` tokens: the
// app's `tone-*` tokens follow the color scheme, which the website ignores.
const LANDING_TONE_TEXT_CLASS_NAMES: Record<LandingTone, string> = {
  falling: "text-landing-tone-4",
  high: "text-landing-tone-1",
};

// Pitch contours on a 20×12 grid, drawn left to right: high stays at the top, falling drops.
const LANDING_TONE_CONTOUR_PATHS: Record<LandingTone, string> = {
  falling: "M2 2 L18 10",
  high: "M2 3 L18 3",
};

function LandingToneContour({ tone }: { tone: LandingTone }) {
  return (
    <svg
      aria-hidden={true}
      className={cn("h-3 w-5 shrink-0", LANDING_TONE_TEXT_CLASS_NAMES[tone])}
      fill="none"
      viewBox="0 0 20 12"
    >
      <path
        d={LANDING_TONE_CONTOUR_PATHS[tone]}
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth={2.5}
      />
    </svg>
  );
}

type LandingPinyinStep = {
  body: string;
  glyph: string;
  /** What this lesson adds: a new final, initial or tone. */
  newSounds: { label: string; tone?: LandingTone }[];
  pinyin: string;
  title: string;
  tone: LandingTone;
};

/** The opening lessons of the first pinyin level, in order, as the Simplified course teaches them. */
const LANDING_PINYIN_STEPS: LandingPinyinStep[] = [
  {
    body: "Your first syllable is a lone final with the falling tone, said like an exclamation: àn ≈ “an!” as in announcer.",
    glyph: "岸",
    newSounds: [{ label: "Final -an" }, { label: "Falling tone", tone: "falling" }],
    pinyin: "àn",
    title: "One final, one tone",
    tone: "falling",
  },
  {
    body: "Then a consonant joins in front. Many initials sound close to English, like g- as in gun.",
    glyph: "干",
    newSounds: [{ label: "Initial g-" }],
    pinyin: "gàn",
    title: "Add an initial",
    tone: "falling",
  },
  {
    body: "The same syllable with the high tone means something else: gàn is “to do”, gān is “dry”.",
    glyph: "干",
    newSounds: [{ label: "High tone", tone: "high" }],
    pinyin: "gān",
    title: "Change the tone",
    tone: "high",
  },
  {
    body: "Two letters, one sound: sh- as in shirt, taught alongside ch- and zh-.",
    glyph: "山",
    newSounds: [{ label: "Initial sh-" }],
    pinyin: "shān",
    title: "Meet sh-",
    tone: "high",
  },
];

/** The pinyin levels that follow, named as the curriculum names them. */
const LANDING_PINYIN_LEVEL_NAMES = [
  "Initials, finals and tones",
  "The final -i",
  "The final -e and the neutral tone",
  "Finals -ang, -iang and -uang",
  "The rising tone",
  "Finals -ong and -iong",
  "The final -iu",
];

function LandingPinyinStepCard({ index, step }: { index: number; step: LandingPinyinStep }) {
  return (
    <WebsiteCard className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-4">
        <WebsiteEyebrow tone="muted">Step {index + 1}</WebsiteEyebrow>
        <span className="text-xs font-medium text-black/40">Level 1 character</span>
      </div>
      <p className="m-0 mt-5 flex items-baseline gap-3">
        <span className="text-[2.6rem] leading-none text-black" lang="zh-Hans">
          {step.glyph}
        </span>
        <span
          className={cn(
            "text-[1.6rem] leading-none font-semibold tracking-[-0.02em]",
            LANDING_TONE_TEXT_CLASS_NAMES[step.tone],
          )}
        >
          {step.pinyin}
        </span>
      </p>
      <WebsiteHeading className="mt-5" level={3} size="label">
        {step.title}
      </WebsiteHeading>
      <WebsiteText className="mt-1.5" size="caption">
        {step.body}
      </WebsiteText>
      <ul className="m-0 mt-auto flex list-none flex-wrap gap-2 p-0 pt-5">
        {step.newSounds.map((sound) => (
          <li
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-2.5 py-1 text-xs font-semibold text-black/70"
            key={sound.label}
          >
            {sound.tone === undefined ? null : <LandingToneContour tone={sound.tone} />}
            {sound.label}
          </li>
        ))}
      </ul>
    </WebsiteCard>
  );
}

/**
 * Pronunciation is built up the same way as characters: the first pinyin lessons take one sound at
 * a time, through characters the learner already met in Level 1.
 */
export function LandingPinyinSection() {
  return (
    <WebsiteSection id="pinyin" tone="canvas">
      <WebsiteContainer>
        <div className="grid gap-5 lg:grid-cols-2 lg:items-end lg:gap-16">
          <div>
            <WebsiteEyebrow>Pinyin from zero</WebsiteEyebrow>
            <WebsiteHeading className="mt-4 max-w-[14ch]" level={2}>
              Even the sounds start from scratch.
            </WebsiteHeading>
          </div>
          <WebsiteText className="max-w-[32rem]" size="lead">
            No wall of syllable charts on day one. Pinyin lessons teach the sounds of characters you
            already know, adding a final, an initial or a tone at a time. A reading stays locked
            until you’ve learned every sound in it.
          </WebsiteText>
        </div>

        <ol className="m-0 mt-12 grid list-none gap-5 p-0 sm:mt-16 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {LANDING_PINYIN_STEPS.map((step, index) => (
            <li key={`${step.glyph}-${step.pinyin}`}>
              <LandingPinyinStepCard index={index} step={step} />
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-col gap-4 sm:mt-14 lg:flex-row lg:items-center lg:gap-8">
          <WebsiteText className="shrink-0" size="caption" tone="default">
            <span className="font-semibold">The first pinyin levels, one layer each:</span>
          </WebsiteText>
          <ol className="m-0 flex list-none flex-wrap gap-2 p-0">
            {LANDING_PINYIN_LEVEL_NAMES.map((name, index) => (
              <li
                className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-black/70 ring-1 ring-black/8"
                key={name}
              >
                <span className="text-website-accent tabular-nums">{index + 1}</span>
                {name}
              </li>
            ))}
          </ol>
        </div>
      </WebsiteContainer>
    </WebsiteSection>
  );
}
